import { Iterable } from 'immutable';

import BudgetAllocation from 'entities/budgetAllocation/model';
import DonationMatchingRule from 'entities/donationMatchingRule/model';
import DonationCreditRule from 'entities/donationCreditRule/model';
import Budget from 'entities/budget/model';

export interface BudgetTotals {
  totalIssued: number;
  totalMatched: number;
  totalContribution?: number;
  totalMatchingLimit?: number;
  totalCredited: number;
  totalCreditingLimit?: number;
  totalCreditExpired: number;
  totalCorporateDonationsAllocation: number;
  totalCorporateDonations: number;
  donationsMatchedCount: number;
  creditingTransactionsCount: number;
  corporateDonationsCount: number;
  volunteerRewardDonationsCount: number;
  totalVolunteerRewardDonations: number;
  totalVolunteerRewardDonationsAllocation: number;
  totalVolunteerRewardDonationsAllocationBalance: number;
  totalUsed: number;
  balance?: number;
  matchingBalance?: number;
  creditUnassigned?: number;
  totalCreditSpent: number;
  totalCreditSpendable: number;
  totalCorporateDonationsAllocationBalance?: number;
}

export const BUDGET_LOW_PERCENTAGE = 0.15;

export const summarizeBudget = (
  budget: Budget,
  rules: Iterable<string, DonationCreditRule | DonationMatchingRule | BudgetAllocation>
) => {
  const budgetRules = rules.filter((rule) => rule.budget === budget.id);
  const budgetTotals: BudgetTotals = budgetRules.reduce(
    (totals, rule) => {
      /* eslint-disable no-param-reassign */
      const ruleIssuedAmount = Number.isFinite(rule?.totalIssuedCurrencies?.[budget.currencyCode])
        ? rule.totalIssuedCurrencies[budget.currencyCode]
        : 0;

      totals.totalIssued += ruleIssuedAmount;

      if (rule instanceof DonationMatchingRule) {
        totals.donationsMatchedCount += rule.donationsMatchedCount;
        totals.totalMatched += ruleIssuedAmount;

        if (Number.isFinite(rule.limitTotal) && Number.isFinite(totals.totalMatchingLimit)) {
          totals.totalMatchingLimit += rule.limitTotal;
        } else {
          totals.totalMatchingLimit = undefined;
        }

        if (budget.limitAssertionType === 'Contribution') {
          const ruleContributionAmount = Number.isFinite(
            rule?.totalContributionCurrencies?.[budget.currencyCode]
          )
            ? rule.totalContributionCurrencies[budget.currencyCode]
            : 0;
          totals.totalContribution += ruleContributionAmount;
        }
      }

      if (rule instanceof DonationCreditRule) {
        totals.creditingTransactionsCount += rule.creditingTransactionsCount;
        totals.totalCredited += ruleIssuedAmount;
        totals.totalCreditExpired += Number.isFinite(
          rule?.totalExpiredCurrencies?.[budget.currencyCode]
        )
          ? rule.totalExpiredCurrencies[budget.currencyCode]
          : 0;
        totals.totalCreditSpent += Number.isFinite(rule?.totalUsedCurrencies?.[budget.currencyCode])
          ? rule.totalUsedCurrencies[budget.currencyCode]
          : 0;
        totals.totalCreditSpendable += Number.isFinite(
          rule?.totalRemainingCurrencies?.[budget.currencyCode]
        )
          ? rule.totalRemainingCurrencies[budget.currencyCode]
          : 0;

        if (Number.isFinite(rule.limitTotal) && Number.isFinite(totals.totalCreditingLimit)) {
          totals.totalCreditingLimit += rule.limitTotal;
        } else {
          totals.totalCreditingLimit = undefined;
        }
      }

      if (rule instanceof BudgetAllocation) {
        const isVolunteerReward = rule.__t === 'VolunteerRewardBudgetAllocation';

        if (isVolunteerReward) {
          totals.volunteerRewardDonationsCount += rule.volunteerRewardDonationsCount;
          totals.totalVolunteerRewardDonations += ruleIssuedAmount;
        } else {
          totals.corporateDonationsCount += rule.corporateDonationsCount;
          totals.totalCorporateDonations += ruleIssuedAmount;
        }

        const totalAllocationKey = isVolunteerReward
          ? 'totalVolunteerRewardDonationsAllocation'
          : 'totalCorporateDonationsAllocation';

        if (Number.isFinite(rule.limitTotal) && Number.isFinite(totals[totalAllocationKey])) {
          totals[totalAllocationKey] += rule.limitTotal;
        } else {
          totals[totalAllocationKey] = undefined;
        }
      }

      /* eslint-enable no-param-reassign */
      return totals;
    },
    {
      totalIssued: 0,
      totalMatched: 0,
      totalMatchingLimit: 0,
      totalContribution: 0,
      totalCredited: 0,
      totalCreditingLimit: 0,
      totalCreditExpired: 0,
      donationsMatchedCount: 0,
      creditingTransactionsCount: 0,
      totalUsed: 0,
      totalCreditSpent: 0,
      totalCreditSpendable: 0,
      totalCorporateDonations: 0,
      totalCorporateDonationsAllocationBalance: 0,
      totalCorporateDonationsAllocation: 0,
      corporateDonationsCount: 0,
      volunteerRewardDonationsCount: 0,
      totalVolunteerRewardDonations: 0,
      totalVolunteerRewardDonationsAllocation: 0,
      totalVolunteerRewardDonationsAllocationBalance: 0,
    }
  );
  budgetTotals.totalUsed = budget.get('_matchedAmountCurrencies')?.[budget.currencyCode] ?? 0;
  budgetTotals.balance = budget.get('limitTotal')
    ? budget.get('limitTotal') - budgetTotals.totalUsed
    : undefined;
  budgetTotals.matchingBalance = Number.isFinite(budgetTotals.totalMatchingLimit)
    ? budgetTotals.totalMatchingLimit - budgetTotals.totalMatched
    : undefined;
  budgetTotals.creditUnassigned = Number.isFinite(budgetTotals.totalCreditingLimit)
    ? budgetTotals.totalCreditingLimit - budgetTotals.totalCredited
    : undefined;
  budgetTotals.totalCorporateDonationsAllocationBalance = Number.isFinite(
    budgetTotals.totalCorporateDonations
  )
    ? budgetTotals.totalCorporateDonationsAllocation - budgetTotals.totalCorporateDonations
    : undefined;

  budgetTotals.totalVolunteerRewardDonationsAllocationBalance = Number.isFinite(
    budgetTotals.totalVolunteerRewardDonations
  )
    ? budgetTotals.totalVolunteerRewardDonationsAllocation -
      budgetTotals.totalVolunteerRewardDonations
    : undefined;

  const now = new Date();
  const budgetInactive =
    (budget.get('endingAt') && budget.get('endingAt') < now) ||
    (budget.get('startingAt') && budget.get('startingAt') > now);

  const budgetBalanceLow =
    budget.get('limitTotal') &&
    !budgetInactive &&
    budgetTotals.balance / budget.get('limitTotal') < BUDGET_LOW_PERCENTAGE;

  return {
    budgetRules,
    budgetTotals,
    budgetInactive,
    budgetBalanceLow,
  };
};
